// StaffProfile — a staff login's own profile (/staffProfile, role 'user').
//
// WHY (owner, 1 Oct 2026, "Concept 2" rule 3): an admin and a doctor could see,
// verify and change their sign-in contacts on their profile ("How you sign
// in"); a staff member had no page at all. This is that page: name, the role
// they hold, the organisation, their photo, and the same SignInContacts block
// the admin profile uses — saving through PUT /staff/me with the one field and
// its proof ({ email, emailProof } / { phone, phoneProof }). The verified
// flags shown are the server's, re-read by updateMyStaffProfile.
//
// Everything is keyed on the TOKEN (/staff/me, /staff/me/image) — never on a
// staff id from the URL — so one employee can never open another's record.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Edit, Loader2, Pencil, Trash2, UserRound, Building2, BadgeCheck, X, Check } from 'lucide-react';
import { getMyStaffProfile, updateMyStaffProfile } from '../redux/staffSlice';
import { docPatCreationCountryList } from '../redux/masterSlice';
import createAuthedAxios from '../hooks/useAxiosAuth';
import useSession from '../hooks/useSession';
import useTenantLoginPolicy from '../hooks/useTenantLoginPolicy';
import SignInContacts from '../components/common/SignInContacts';
import CommonLoader from '../components/CommonLoader';
import { getCurrentOrg } from '../utils/orgWords';
import { formatFirstName, formatSurname } from '../utils/personFields';

const VALID_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const NAME_MAX = 25;

const messageOf = (err, fallback, lang) => {
    const m = err?.error;
    if (typeof m === 'string') return m;
    if (m && typeof m === 'object') return m[lang] || m.en || m.fr || fallback;
    return fallback;
};

export default function StaffProfile() {
    const { t, i18n } = useTranslation();
    const lang = i18n?.language?.startsWith('fr') ? 'fr' : 'en';
    const dispatch = useDispatch();
    const session = useSession();
    const me = useSelector((s) => s.staff?.myProfile);
    const countryTimezoneList = useSelector((s) => s.master?.countryTimezoneList);
    const policy = useTenantLoginPolicy(me?.tenantId || session.tenantId || '');
    // The photo is multipart, so it cannot ride the JSON thunks. A factory,
    // not a hook (see AddUser).
    const api = useMemo(() => createAuthedAxios(), []);
    const fileInputRef = useRef(null);

    const [loadFailed, setLoadFailed] = useState(false);
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({ name: '', surname: '' });
    const [nameError, setNameError] = useState('');
    const [saving, setSaving] = useState(false);
    const [photoBusy, setPhotoBusy] = useState(false);

    useEffect(() => {
        dispatch(getMyStaffProfile()).unwrap().catch(() => setLoadFailed(true));
        if (!countryTimezoneList || countryTimezoneList.length === 0) dispatch(docPatCreationCountryList());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const country = (countryTimezoneList || []).find((c) => c.countryId === me?.countryId) || null;
    const orgName = getCurrentOrg()?.name || '';
    const roleName = me?.roleName || session.staffRoleName || t('staffProfile.role_staff', 'Staff');
    const fullName = [me?.name, me?.surname].filter(Boolean).join(' ');

    const startEdit = () => {
        setForm({ name: me?.name || '', surname: me?.surname || '' });
        setNameError('');
        setEditing(true);
    };

    const saveNames = async () => {
        const name = form.name.trim();
        if (!name) {
            setNameError(t('staffProfile.name_required', 'Name is required'));
            return;
        }
        setSaving(true);
        try {
            await dispatch(updateMyStaffProfile({ name, surname: form.surname.trim() || null })).unwrap();
            toast.success(t('staffProfile.saved', 'Profile updated'));
            setEditing(false);
        } catch (err) {
            toast.error(messageOf(err, t('staffProfile.save_failed', 'Could not save your profile'), lang));
        } finally {
            setSaving(false);
        }
    };

    const pickPhoto = async (e) => {
        const file = e.target.files?.[0];
        if (fileInputRef.current) fileInputRef.current.value = '';
        if (!file) return;
        if (!VALID_IMAGE_TYPES.includes(file.type)) {
            toast.warn(t('userpage.invalid_image_type', 'Please select a JPG, PNG or WEBP image'));
            return;
        }
        if (file.size > MAX_IMAGE_SIZE) {
            toast.warn(t('userpage.image_too_large', 'Image must be 5MB or smaller'));
            return;
        }
        setPhotoBusy(true);
        try {
            const body = new FormData();
            body.append('file', file);
            await api.post('/staff/me/image', body, { headers: { 'Content-Type': 'multipart/form-data' } });
            await dispatch(getMyStaffProfile()).unwrap();
            toast.success(t('staffProfile.photo_saved', 'Photo updated'));
        } catch {
            toast.error(t('staffProfile.photo_failed', 'The photo could not be uploaded.'));
        } finally {
            setPhotoBusy(false);
        }
    };

    const removePhoto = async () => {
        setPhotoBusy(true);
        try {
            await api.delete('/staff/me/image');
            await dispatch(getMyStaffProfile()).unwrap();
            toast.success(t('staffProfile.photo_removed', 'Photo removed'));
        } catch {
            toast.error(t('staffProfile.photo_remove_failed', 'The photo could not be removed.'));
        } finally {
            setPhotoBusy(false);
        }
    };

    if (!me) {
        return (
            <div data-testid="staff-profile-container" className="bg-white rounded-2xl p-4 shadow-xl border border-gray-100 sm:p-6 mx-auto">
                {loadFailed ? (
                    <p data-testid="staff-profile-load-failed" className="py-12 text-center text-sm text-gray-500">
                        {t('staffProfile.load_failed', 'Your profile could not be loaded. Please try again.')}
                    </p>
                ) : (
                    <div data-testid="staff-profile-loading" className="flex flex-col items-center justify-center py-12">
                        <CommonLoader />
                    </div>
                )}
            </div>
        );
    }

    const inputCls = (error) =>
        `w-full p-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${error ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD] focus:border-[#4ABDBD]'}`;

    return (
        <div data-testid="staff-profile-container" className="bg-white rounded-2xl p-4 shadow-xl border border-gray-100 sm:p-6 mx-auto">
            <div className="mb-6">
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent ml-2">
                    {t('staffProfile.title', 'My profile')}
                </h1>
            </div>

            <div className="p-4 pb-0">
                {/* Photo */}
                <div data-testid="staff-profile-avatar-section" className="flex flex-col items-center gap-3 my-6">
                    <div className="relative">
                        <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gray-100 flex items-center justify-center border-4 border-[#4ABDBD]/30 overflow-hidden">
                            {me.image ? (
                                <img data-testid="staff-profile-avatar-image" src={me.image} alt={t('staffProfile.photo_alt', 'Profile photo')} className="w-full h-full object-cover" />
                            ) : (
                                <UserRound className="w-12 h-12 text-gray-300" />
                            )}
                        </div>
                        <button
                            type="button"
                            data-testid="staff-profile-avatar-edit"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={photoBusy}
                            title={t('staffProfile.change_photo', 'Change photo')}
                            className="absolute bottom-0 right-0 p-1.5 rounded-full bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] text-white shadow-md hover:shadow-lg disabled:opacity-60 cursor-pointer"
                        >
                            {photoBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pencil className="w-4 h-4" />}
                        </button>
                        {me.image && (
                            <button
                                type="button"
                                data-testid="staff-profile-avatar-remove"
                                onClick={removePhoto}
                                disabled={photoBusy}
                                title={t('staffProfile.remove_photo', 'Remove photo')}
                                className="absolute bottom-0 left-0 p-1.5 rounded-full bg-white text-red-500 border border-gray-200 shadow-md hover:bg-red-50 disabled:opacity-60 cursor-pointer"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                    <input ref={fileInputRef} data-testid="staff-profile-avatar-input" type="file" accept="image/*" className="hidden" onChange={pickPhoto} />
                    <p data-testid="staff-profile-full-name" className="text-lg font-semibold text-gray-800">{fullName}</p>
                    <div className="flex flex-wrap items-center justify-center gap-2">
                        <span data-testid="staff-profile-role" className="inline-flex items-center gap-1.5 rounded-full bg-[#4ABDBD]/10 px-3 py-1 text-xs font-semibold text-[#2D8A8A]">
                            <BadgeCheck className="w-3.5 h-3.5" /> {roleName}
                        </span>
                        {orgName && (
                            <span data-testid="staff-profile-org" className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">
                                <Building2 className="w-3.5 h-3.5" /> {orgName}
                            </span>
                        )}
                    </div>
                </div>

                {/* Personal details */}
                <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                    <h2 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                        {t('staffProfile.personal_details', 'Personal details')}
                    </h2>
                    {!editing && (
                        <button
                            type="button"
                            data-testid="staff-profile-edit"
                            onClick={startEdit}
                            className="flex items-center gap-2 w-full sm:w-auto justify-center cursor-pointer px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm"
                        >
                            <Edit className="h-5 w-5" /> {t('staffProfile.edit', 'Edit')}
                        </button>
                    )}
                </div>

                {editing ? (
                    <div data-testid="staff-profile-form" className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                        <div>
                            <label className="block text-gray-400 font-medium mb-1">{t('staffProfile.first_name', 'First name')} <span className="text-red-500">*</span></label>
                            <input
                                data-testid="staff-profile-name-input"
                                value={form.name}
                                maxLength={NAME_MAX}
                                onChange={(e) => { setForm((f) => ({ ...f, name: formatFirstName(e.target.value) })); setNameError(''); }}
                                className={inputCls(!!nameError)}
                            />
                            {nameError && <p data-testid="staff-profile-name-error" className="text-red-500 text-sm mt-1">{nameError}</p>}
                        </div>
                        <div>
                            <label className="block text-gray-400 font-medium mb-1">{t('staffProfile.surname', 'Surname')}</label>
                            <input
                                data-testid="staff-profile-surname-input"
                                value={form.surname}
                                maxLength={NAME_MAX}
                                onChange={(e) => setForm((f) => ({ ...f, surname: formatSurname(e.target.value) }))}
                                className={inputCls(false)}
                            />
                        </div>
                        <div className="sm:col-span-2 flex justify-end gap-2">
                            <button
                                type="button"
                                data-testid="staff-profile-cancel"
                                onClick={() => setEditing(false)}
                                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-50 cursor-pointer"
                            >
                                <X className="w-4 h-4" /> {t('staffProfile.cancel', 'Cancel')}
                            </button>
                            <button
                                type="button"
                                data-testid="staff-profile-save"
                                onClick={saveNames}
                                disabled={saving}
                                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-xl text-white font-semibold bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] hover:shadow-lg disabled:opacity-60 cursor-pointer"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                {t('staffProfile.save', 'Save changes')}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div data-testid="staff-profile-details" className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2">
                        <div className="mb-4">
                            <label className="block text-gray-400 font-medium mb-1">{t('staffProfile.first_name', 'First name')}</label>
                            <p className="text-gray-800" data-testid="staff-profile-name">{me.name || '-'}</p>
                        </div>
                        <div className="mb-4">
                            <label className="block text-gray-400 font-medium mb-1">{t('staffProfile.surname', 'Surname')}</label>
                            <p className="text-gray-800" data-testid="staff-profile-surname">{me.surname || '-'}</p>
                        </div>
                        <div className="mb-4">
                            <label className="block text-gray-400 font-medium mb-1">{t('staffProfile.role', 'Role')}</label>
                            <p className="text-gray-800">{roleName}</p>
                        </div>
                        <div className="mb-4">
                            <label className="block text-gray-400 font-medium mb-1">{t('staffProfile.organisation', 'Organisation')}</label>
                            <p className="text-gray-800">{orgName || '-'}</p>
                        </div>
                        {me.designation && (
                            <div className="mb-4">
                                <label className="block text-gray-400 font-medium mb-1">{t('userpage.designation', 'Designation')}</label>
                                <p className="text-gray-800" data-testid="staff-profile-designation">{me.designation}</p>
                            </div>
                        )}
                        {Array.isArray(me.departments) && me.departments.length > 0 && (
                            <div className="mb-4">
                                <label className="block text-gray-400 font-medium mb-1">{t('userpage.departments', 'Departments / counters')}</label>
                                <p className="text-gray-800">{me.departments.join(', ')}</p>
                            </div>
                        )}
                    </div>
                )}

                {/* HOW YOU SIGN IN — the same block as the admin's profile, with
                    the save going to PUT /staff/me (one field + its proof). */}
                {!editing && (
                    <SignInContacts
                        subject={me}
                        canWrite={true}
                        own={true}
                        phoneMin={country?.mobileMinLength || 9}
                        phoneMax={country?.mobileMaxLength || 10}
                        testIdPrefix="staff-signin"
                        policy={policy}
                        onSave={(body) => dispatch(updateMyStaffProfile(body)).unwrap()}
                    />
                )}
            </div>
        </div>
    );
}
